import { FluentBundle, FluentResource } from '@fluent/bundle';

// ISO 8601 in UTC, as the server's utc_time writes it: a date never follows the
// reader's language or clock. Each style is a slice of "2026-10-02T09:05:07".
const UTC_STYLES = { date: [0, 10], minutes: [0, 16], seconds: [0, 19], clock: [11, 16] };

export function utcTimestamp(value, style = 'seconds') {
    const [start, end] = UTC_STYLES[style] || UTC_STYLES.seconds;
    return new Date(value).toISOString().slice(start, end).replace('T', ' ') + ' UTC';
}

// A separate factory keeps fallback behavior testable without adding browser globals.
export function createI18n(locale, resources) {
    const bundles = [];
    for (const language of new Set([locale, 'en'])) {
        const source = resources[language];
        if (!source) continue;
        const bundle = new FluentBundle(language, { useIsolating: false });
        bundle.addResource(new FluentResource(source));
        bundles.push(bundle);
    }
    const missing = new Set();
    const pending = new Set();
    function reportFailure(id) {
        if (missing.has(id) || missing.size >= 1000) return;
        missing.add(id);
        if (typeof globalThis.wosReportError === 'function') {
            globalThis.wosReportError(new Error('Localization fallback'), 'i18n.format', { locale, message_id: id.slice(0, 120) });
        } else {
            pending.add(id);
        }
    }
    globalThis.document?.addEventListener?.('DOMContentLoaded', () => {
        for (const id of pending) {
            globalThis.wosReportError?.(new Error('Localization fallback'), 'i18n.format', { locale, message_id: id.slice(0, 120) });
        }
        pending.clear();
    }, { once: true });
    // "message-id.attribute" formats one attribute, like the server does.
    function t(id, arguments_ = {}) {
        const [messageId, attribute] = id.split('.', 2);
        for (const bundle of bundles) {
            const message = bundle.getMessage(messageId);
            const pattern = attribute ? message?.attributes?.[attribute] : message?.value;
            if (!pattern) {
                reportFailure(id);
                continue;
            }
            const errors = [];
            try {
                const text = bundle.formatPattern(pattern, arguments_, errors);
                if (!errors.length) return text;
            } catch {
                // Formatting exceptions and Fluent error lists share safe fallback.
            }
            reportFailure(id);
        }
        return 'Unavailable';
    }
    function localizedFetch(input, options = {}) {
        const url = new URL(input instanceof Request ? input.url : input, globalThis.location.href);
        if (url.origin !== globalThis.location.origin) return globalThis.fetch(input, options);
        const headers = new Headers(options.headers || (input instanceof Request ? input.headers : {}));
        headers.set('X-WOS-Locale', locale);
        return globalThis.fetch(input, { ...options, headers });
    }
    return Object.freeze({
        locale,
        contract: 1,
        degraded: false,
        t,
        fetch: localizedFetch,
        number: (value, options = {}) => new Intl.NumberFormat(locale, options).format(value),
        currency: (value, currency) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value),
        timestamp: utcTimestamp,
    });
}
